import { css, html, LitElement, TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { avatarStyle, profileInitial } from "../shared/colors.js";
import { AcceptShareResponse, PublicShareInvitation } from "../shared/api.js";
import { Profile } from "../shared/type.app.js";
import { wait } from "../shared/util.wait.js";
import { getAuthUser, isAuthConfigured, loginInstructor, whenAuthReady } from "./auth0.js";
import { WarningEvent } from "./event.warning.js";
import { incomingShareFromPath, navigate } from "./nav.js";
import { readingBeeApi } from "./sql-api.js";
import { appStore } from "./store.js";
import { globalStyles } from "./styles.global.js";
import { dispatch } from "./util.events.js";

const HEXES: Array<[number, number]> = [
  [0, 0],
  [1, 0],
  [0, 1],
  [-1, 1],
  [-1, 0],
  [0, -1],
  [1, -1],
  [2, -1],
  [2, 0],
  [1, 1],
  [-1, 2],
  [-2, 1],
  [-2, 0],
  [-1, -1],
  [1, -2],
  [0, -2],
];

const HEX_SIZE = 46;
const LOAD_HOLD_MS = 1800;
const REVEAL_HOLD_MS = 1300;

const beeIcon = html`
  <svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <ellipse class="wing wing-l" cx="7.5" cy="13" rx="7" ry="5.2" fill="#f4ead5" />
    <ellipse class="wing wing-r" cx="24.5" cy="13" rx="7" ry="5.2" fill="#f4ead5" />
    <ellipse cx="16" cy="19" rx="7.4" ry="9.2" fill="currentColor" />
    <path
      d="M11 16.5h10M11 20.2h10M11 23.8h10"
      stroke="#1a1408"
      stroke-width="1.35"
      stroke-linecap="round"
      opacity="0.38" />
    <circle cx="13.2" cy="13.4" r="1.15" fill="#1a1408" />
    <circle cx="18.8" cy="13.4" r="1.15" fill="#1a1408" />
  </svg>
`;

@customElement("reading-bee-accept-share")
export class ReadingBeeAcceptShare extends LitElement {
  static override styles = [
    globalStyles,
    css`
      :host {
        display: block;
        height: 100%;
        min-height: 100%;
      }

      .stage {
        position: relative;
        isolation: isolate;
        overflow: hidden;
        min-height: 100%;
        height: 100%;
        display: grid;
        place-items: center;
        padding: calc(1.5rem + var(--inset-top)) 1.25rem calc(1.5rem + env(safe-area-inset-bottom));
        color: var(--color-primary-text);
      }

      .glow {
        position: absolute;
        inset: -20% -10% auto;
        height: 70%;
        background: radial-gradient(ellipse at 50% 30%, rgba(232, 184, 74, 0.2), transparent 62%);
        pointer-events: none;
        z-index: 0;
      }

      .hive {
        position: absolute;
        left: 50%;
        top: 44%;
        width: 0;
        height: 0;
        z-index: 1;
        pointer-events: none;
      }

      .hex {
        position: absolute;
        width: 72px;
        height: 82px;
        margin: -41px 0 0 -36px;
        left: var(--x);
        top: var(--y);
        clip-path: polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%);
        background: rgba(232, 184, 74, 0.09);
        box-shadow: inset 0 0 0 1px rgba(232, 184, 74, 0.2);
        opacity: 0;
        transform: scale(0.45);
        animation:
          hex-in 800ms cubic-bezier(0.2, 0, 0, 1) var(--d) forwards,
          hex-breathe 4s ease-in-out calc(var(--d) + 800ms) infinite;
      }

      .stage.is-ready .hex {
        animation: hex-recede 640ms cubic-bezier(0.2, 0, 0, 1) forwards;
      }

      .flight {
        position: absolute;
        left: 50%;
        top: 44%;
        width: 210px;
        height: 210px;
        margin: -105px 0 0 -105px;
        z-index: 2;
        pointer-events: none;
        animation: orbit 7.5s linear infinite;
      }

      .bee {
        position: absolute;
        top: 0;
        left: 50%;
        width: 34px;
        height: 34px;
        margin-left: -17px;
        color: var(--color-1);
        filter: drop-shadow(0 8px 12px rgba(0, 0, 0, 0.35));
        animation: counter-orbit 7.5s linear infinite;
      }

      .bee svg {
        width: 34px;
        height: 34px;
        display: block;
      }

      .wing-l,
      .wing-r {
        transform-box: fill-box;
        transform-origin: center;
        animation: flap 240ms ease-in-out infinite;
      }

      .wing-r {
        animation-delay: 120ms;
      }

      .stage.is-ready .flight {
        animation: bee-away 700ms cubic-bezier(0.2, 0, 0, 1) forwards;
      }

      .hero {
        position: relative;
        z-index: 3;
        display: grid;
        justify-items: center;
        text-align: center;
        gap: 0.55rem;
        width: min(22rem, 100%);
      }

      .avatar-wrap {
        position: relative;
        width: 112px;
        height: 112px;
        margin-bottom: 0.45rem;
      }

      .ring {
        position: absolute;
        inset: -7px;
        border-radius: 50%;
        border: 2px solid rgba(232, 184, 74, 0.18);
        border-top-color: var(--color-1);
        animation: spin 1.1s linear infinite;
      }

      .stage.is-ready .ring {
        animation: ring-settle 520ms cubic-bezier(0.2, 0, 0, 1) forwards;
        border-color: rgba(232, 184, 74, 0.55);
      }

      .avatar {
        width: 112px;
        height: 112px;
        border-radius: 50%;
        display: grid;
        place-items: center;
        font-weight: 800;
        font-size: 2.6rem;
        line-height: 1;
        box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
        transform: scale(0.86);
        opacity: 0.55;
        transition:
          transform 520ms cubic-bezier(0.16, 1, 0.3, 1),
          opacity 420ms ease;
      }

      .avatar.is-ghost {
        background: #1a1713;
        color: var(--color-1);
        box-shadow: inset 0 0 0 2px rgba(232, 184, 74, 0.22);
        font-size: 0;
      }

      .stage.is-ready .avatar {
        transform: scale(1);
        opacity: 1;
      }

      .kicker {
        margin: 0;
        font-size: 0.72rem;
        font-weight: 800;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: var(--color-1);
      }

      h1 {
        margin: 0;
        font-size: clamp(1.6rem, 6vw, 2.15rem);
        letter-spacing: -0.04em;
        line-height: 1.1;
      }

      .detail {
        margin: 0;
        max-width: 26ch;
        font-size: 1rem;
      }

      .card {
        position: relative;
        z-index: 3;
        width: min(420px, 100%);
        display: grid;
        gap: 0.9rem;
        padding: 1.2rem;
        border-radius: 22px;
        background: #1a1713;
        border: 1px solid var(--color-panel-border);
      }

      .card h1 {
        font-size: 1.25rem;
        letter-spacing: -0.02em;
      }

      .primary-btn,
      .ghost-btn {
        width: 100%;
      }

      @keyframes hex-in {
        to {
          opacity: 1;
          transform: scale(1);
        }
      }

      @keyframes hex-breathe {
        0%,
        100% {
          opacity: 0.85;
        }
        50% {
          opacity: 1;
        }
      }

      @keyframes hex-recede {
        to {
          opacity: 0.22;
          transform: scale(0.92);
        }
      }

      @keyframes orbit {
        to {
          transform: rotate(360deg);
        }
      }

      @keyframes counter-orbit {
        to {
          transform: rotate(-360deg);
        }
      }

      @keyframes flap {
        0%,
        100% {
          transform: scaleY(1);
        }
        50% {
          transform: scaleY(0.55);
        }
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      @keyframes ring-settle {
        to {
          transform: scale(1.04);
          opacity: 0.9;
        }
      }

      @keyframes bee-away {
        to {
          opacity: 0;
          transform: translate(42px, -64px) scale(0.6);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .hex,
        .flight,
        .bee,
        .wing-l,
        .wing-r,
        .ring {
          animation: none !important;
        }

        .hex {
          opacity: 0.7;
          transform: scale(1);
        }

        .avatar {
          transform: none;
          opacity: 1;
        }
      }
    `,
  ];

  @state() private status: "loading" | "need-sign-in" | "consent" | "error" | "ready" = "loading";
  @state() private message = "";
  @state() private invitation: PublicShareInvitation | null = null;
  @state() private guest: Profile | null = null;
  private shareToken: string | null = null;
  private cancelled = false;

  override connectedCallback(): void {
    super.connectedCallback();
    this.cancelled = false;
    void this.accept();
  }

  override disconnectedCallback(): void {
    this.cancelled = true;
    super.disconnectedCallback();
  }

  override render(): TemplateResult {
    const waiting = this.status === "loading" || this.status === "ready";
    const showHive = this.status !== "error";
    return html`
      <div class="stage ${this.status === "ready" ? "is-ready" : ""}">
        <div class="glow"></div>
        ${showHive ? this.hive() : ""}
        ${
          waiting
            ? html`
                <div class="flight" aria-hidden="true">
                  <div class="bee">${beeIcon}</div>
                </div>
              `
            : ""
        }
        ${waiting ? this.hero() : this.panel()}
      </div>
    `;
  }

  private hive(): TemplateResult {
    const sqrt3 = Math.sqrt(3);
    return html`
      <div class="hive" aria-hidden="true">
        ${HEXES.map(([q, r], index) => {
          const x = HEX_SIZE * (sqrt3 * q + (sqrt3 / 2) * r);
          const y = HEX_SIZE * 1.5 * r;
          return html`
            <span class="hex" style="--x:${x}px;--y:${y}px;--d:${index * 55}ms"></span>
          `;
        })}
      </div>
    `;
  }

  private hero(): TemplateResult {
    const profile = this.guest;
    const ready = this.status === "ready" && Boolean(profile);
    return html`
      <div class="hero" role="status" aria-live="polite">
        <div class="avatar-wrap">
          <span class="ring"></span>
          ${
            profile
              ? html`
                  <div class="avatar" style=${avatarStyle(profile.primaryColor, profile.secondaryColor)}>
                    ${profileInitial(profile.name)}
                  </div>
                `
              : html`
                  <div class="avatar is-ghost"></div>
                `
          }
        </div>
        <p class="kicker">${ready ? "Now reading" : "Shared profile"}</p>
        <h1>${ready && profile ? profile.name : "Welcoming a reader"}</h1>
        <p class="detail">${ready ? "Opening their book" : "Adding them to your household"}</p>
      </div>
    `;
  }

  private panel(): TemplateResult {
    if (this.status === "need-sign-in") {
      return html`
        <div class="card">
          <h1>Shared profile</h1>
          <p>
            ${this.invitation?.studentName ?? "This student"} was shared with you by
            ${this.invitation?.ownerDisplayName ?? "another instructor"}.
          </p>
          <p>Sign in with the invited email address to review and accept this invitation.</p>
          <button class="primary-btn" ?disabled=${!isAuthConfigured()} @click=${this.signIn}>Sign in</button>
        </div>
      `;
    }
    if (this.status === "consent") {
      return html`
        <div class="card">
          <h1>Accept shared profile?</h1>
          <p>${this.invitation?.studentName ?? "This student"} will appear in your Reading Bee account.</p>
          <p>
            Permission: ${this.invitation?.permission === "contribute" ? "Can contribute" : "View only"}. Historical
            data remains owned by the instructor who shared it.
          </p>
          <button class="primary-btn" @click=${this.confirmAcceptance}>Accept invitation</button>
          <button class="ghost-btn" @click=${() => navigate("reading")}>Decline</button>
        </div>
      `;
    }
    return html`
      <div class="card">
        <h1>Shared profile</h1>
        <p>${this.message || "This share link could not be opened."}</p>
        <button class="ghost-btn" @click=${() => navigate("reading")}>Back</button>
      </div>
    `;
  }

  private signIn = async (): Promise<void> => {
    const share = incomingShareFromPath();
    try {
      await loginInstructor(share ? `/share/${encodeURIComponent(share.token)}` : "/");
    } catch {
      dispatch(this, WarningEvent("Could not start sign-in"));
    }
  };

  private accept = async (): Promise<void> => {
    await whenAuthReady();
    const share = incomingShareFromPath();
    if (!share) {
      this.status = "error";
      this.message = "This share link is incomplete.";
      return;
    }
    try {
      this.shareToken = share.token;
      this.invitation = await readingBeeApi.getShareInvitation(share.token);
      if (this.invitation.status !== "pending") {
        this.status = "error";
        this.message = "This invitation is no longer available.";
        return;
      }
      if (!getAuthUser()) {
        this.status = "need-sign-in";
        return;
      }
      this.status = "consent";
    } catch (error) {
      this.status = "error";
      this.message = error instanceof Error ? error.message : "This invitation could not be loaded.";
    }
  };

  private confirmAcceptance = async (): Promise<void> => {
    const began = Date.now();
    if (!this.shareToken || !this.invitation || !getAuthUser()) return;
    this.status = "loading";
    try {
      const accepted: AcceptShareResponse = await readingBeeApi.acceptShare(this.shareToken);
      const profile = appStore.acceptIncomingSummary(
        accepted.student,
        accepted.ownerId,
        accepted.shareId,
        accepted.permission,
        accepted.ownerDisplayName,
      );
      if (this.cancelled) return;
      await this.holdForShow(began, LOAD_HOLD_MS);
      if (this.cancelled) return;
      this.guest = profile;
      await this.updateComplete;
      await wait(this.prefersReducedMotion() ? 0 : 80);
      if (this.cancelled) return;
      this.status = "ready";
      await wait(this.prefersReducedMotion() ? 200 : REVEAL_HOLD_MS);
      if (this.cancelled) return;
      navigate("reading", undefined, {
        replace: true,
        motion: this.prefersReducedMotion() ? "none" : "fade",
      });
    } catch (error) {
      if (this.cancelled) return;
      this.status = "error";
      this.message = error instanceof Error ? error.message : "You do not have access to this shared profile.";
    }
  };

  private holdForShow = async (began: number, minMs: number): Promise<void> => {
    if (this.prefersReducedMotion()) return;
    const remaining = minMs - (Date.now() - began);
    if (remaining > 0) await wait(remaining);
  };

  private prefersReducedMotion(): boolean {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "reading-bee-accept-share": ReadingBeeAcceptShare;
  }
}
